import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import {
  Plus,
  Pencil,
  Trash2,
  Package,
  Droplets,
  Flame,
  Gauge,
  Thermometer,
  Zap,
  Wind,
  Layers,
  Hash,
  Loader2,
  X,
  Check,
  ClipboardList,
  Search
} from "lucide-react";

const ICON_MAP = {
  droplets: Droplets,
  flame: Flame,
  gauge: Gauge,
  thermometer: Thermometer,
  zap: Zap,
  wind: Wind,
};

export default function ProductsPage() {
  const { token } = useAuth();
  const [products, setProducts] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const defaultTolerance = {
    defect_sensitivity: "normal",
    ocr_match_mode: "format_only",
    assembly_check: "strict",
    calibration_tolerance_percent: 5,
    custom_rules: ""
  };

  const [formData, setFormData] = useState({
    name: "",
    model_name: "",
    report_name: "",
    description: "",
    product_type_id: "",
    power_options: null,
    test_input_fields: [],
    field_option_config: {},
    disabled_fields: [],
    tolerance_config: { ...defaultTolerance },
    qc_session_mappings: {},
  });
  const [newField, setNewField] = useState({ label: "", type: "text", required: true, placeholder: "", options: [] });
  const [newFieldOption, setNewFieldOption] = useState("");

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchData = useCallback(async () => {
    try {
      const [productsRes, typesRes] = await Promise.all([
        axios.get(`${API}/products`, { headers }),
        axios.get(`${API}/product-types`, { headers })
      ]);
      setProducts(productsRes.data);
      setProductTypes(typesRes.data);
      
      // Set default product type
      if (typesRes.data.length > 0) {
        setFormData(prev => prev.product_type_id ? prev : { ...prev, product_type_id: typesRes.data[0].id });
      }
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const BUILTIN_SESSION_FIELD_KEYS = ["serial_number", "tester_name", "power_supply"];

  // Drop allowed-option values that no longer exist on the field, and entries for fields
  // that no longer exist at all — keeps saved config from carrying stale options/fields forward.
  const reconcileFieldOptionConfig = (liveType, oldConfig) => {
    const config = {};
    for (const f of (liveType?.default_test_input_fields || [])) {
      if (f.type === "select" && f.options?.length > 0) {
        const previouslyAllowed = oldConfig?.[f.key];
        config[f.key] = previouslyAllowed
          ? previouslyAllowed.filter(opt => f.options.includes(opt))
          : [...f.options];
      }
    }
    return config;
  };

  // Drop disabled_fields entries for keys that no longer exist on the live type.
  const reconcileDisabledFields = (liveType, oldDisabled) => {
    const liveKeys = new Set((liveType?.default_test_input_fields || []).map(f => f.key));
    return (oldDisabled || []).filter(k => liveKeys.has(k));
  };

  // Drop mappings pointing at a custom field key that no longer exists on the live type.
  const reconcileQcSessionMappings = (liveType, oldMappings) => {
    const liveKeys = new Set((liveType?.default_test_input_fields || []).map(f => f.key));
    const next = {};
    for (const [rowId, fieldKey] of Object.entries(oldMappings || {})) {
      if (BUILTIN_SESSION_FIELD_KEYS.includes(fieldKey) || liveKeys.has(fieldKey)) {
        next[rowId] = fieldKey;
      }
    }
    return next;
  };

  const handleOpenDialog = (product = null) => {
    if (product) {
      setEditingProduct(product);
      const liveType = productTypes.find(t => t.id === product.product_type_id);
      setFormData({
        name: product.name,
        model_name: product.model_name || "",
        report_name: product.report_name || "",
        description: product.description,
        product_type_id: product.product_type_id || "",
        power_options: product.power_options || null,
        test_input_fields: liveType?.default_test_input_fields || [],
        field_option_config: reconcileFieldOptionConfig(liveType, product.field_option_config),
        disabled_fields: reconcileDisabledFields(liveType, product.disabled_fields),
        tolerance_config: product.tolerance_config || { ...defaultTolerance },
        qc_session_mappings: reconcileQcSessionMappings(liveType, product.qc_session_mappings),
      });
    } else {
      setEditingProduct(null);
      const defaultType = productTypes[0];
      setFormData({
        name: "",
        model_name: "",
        report_name: "",
        description: "",
        product_type_id: defaultType?.id || "",
        power_options: defaultType?.default_power_options || null,
        test_input_fields: defaultType?.default_test_input_fields || [],
        field_option_config: buildDefaultFieldOptionConfig(defaultType),
        disabled_fields: [],
        tolerance_config: { ...defaultTolerance },
        qc_session_mappings: {},
      });
    }
    setNewField({ label: "", type: "text", required: true, placeholder: "", options: [] });
    setNewFieldOption("");
    setShowDialog(true);
  };

  const buildDefaultFieldOptionConfig = (type) => {
    const config = {};
    for (const f of (type?.default_test_input_fields || [])) {
      if (f.type === "select" && f.options?.length > 0) {
        config[f.key] = [...f.options];
      }
    }
    return config;
  };

  const handleProductTypeChange = (typeId) => {
    const selectedType = productTypes.find(t => t.id === typeId);
    setFormData({
      ...formData,
      product_type_id: typeId,
      power_options: selectedType?.default_power_options || null,
      test_input_fields: selectedType?.default_test_input_fields || [],
      field_option_config: buildDefaultFieldOptionConfig(selectedType),
      qc_session_mappings: {},
    });
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error("Product name is required");
      return;
    }
    if (!formData.product_type_id) {
      toast.error("Please select a product type");
      return;
    }

    setSubmitting(true);
    try {
      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, formData, { headers });
        toast.success("Product updated");
      } else {
        await axios.post(`${API}/products`, formData, { headers });
        toast.success("Product created");
      }
      setShowDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure? This will also delete all verification steps.")) return;
    
    try {
      await axios.delete(`${API}/products/${id}`, { headers });
      toast.success("Product deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete product");
    }
  };

  const togglePowerOption = (power) => {
    const current = formData.power_options || [];
    if (current.includes(power)) {
      if (current.length > 1) {
        setFormData({ ...formData, power_options: current.filter(p => p !== power) });
      }
    } else {
      setFormData({ ...formData, power_options: [...current, power] });
    }
  };

  const getIconForType = (product) => {
    const type = productTypes.find(t => t.id === product.product_type_id);
    const iconName = type?.icon || "droplets";
    return ICON_MAP[iconName] || Layers;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  const selectedType = productTypes.find(t => t.id === formData.product_type_id);

  const filteredProducts = products.filter((product) => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return true;
    return (
      product.name?.toLowerCase().includes(q) ||
      product.model_name?.toLowerCase().includes(q) ||
      product.description?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-6" data-testid="products-page">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Products</h2>
          <p className="text-muted-foreground">Manage products for verification</p>
        </div>

        <div className="relative w-64 shrink-0">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search products…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-8"
            data-testid="products-search-input"
          />
        </div>

        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild>
            <Button onClick={() => handleOpenDialog()} disabled={productTypes.length === 0} data-testid="add-product-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Product
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingProduct ? "Edit Product" : "Add New Product"}</DialogTitle>
              <DialogDescription>
                Define a product and its configuration options
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="product-type">Product Type</Label>
                <Select
                  value={formData.product_type_id}
                  onValueChange={handleProductTypeChange}
                >
                  <SelectTrigger id="product-type" data-testid="product-type-select">
                    <SelectValue placeholder="Select product type" />
                  </SelectTrigger>
                  <SelectContent>
                    {productTypes.map((type) => {
                      const IconComp = ICON_MAP[type.icon] || Layers;
                      return (
                        <SelectItem key={type.id} value={type.id}>
                          <div className="flex items-center gap-2">
                            <IconComp className="w-4 h-4" />
                            {type.name}
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                {productTypes.length === 0 && (
                  <p className="text-xs text-amber-600">No product types defined. Please add product types first.</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="name">Product Name</Label>
                <Input
                  id="name"
                  placeholder="e.g., Water Quality Analyzer WQA-3000"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="product-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="model-name">
                  Model Name
                  <span className="text-xs text-muted-foreground font-normal ml-2">Used in QC report footer</span>
                </Label>
                <Input
                  id="model-name"
                  placeholder="e.g., SGP1"
                  value={formData.model_name}
                  onChange={(e) => setFormData({ ...formData, model_name: e.target.value })}
                  className="font-mono"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Describe the product..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  data-testid="product-description-input"
                />
              </div>

              {selectedType && selectedType.default_power_options && selectedType.default_power_options.length > 0 && (
                <div className="space-y-2">
                  <Label>Power Supply Options</Label>
                  <div className="flex gap-2 flex-wrap">
                    {selectedType.default_power_options.map((power) => (
                      <Button
                        key={power}
                        type="button"
                        variant={(formData.power_options || []).includes(power) ? "default" : "outline"}
                        size="sm"
                        onClick={() => togglePowerOption(power)}
                        data-testid={`power-option-${power.replace(/\s+/g, '-')}`}
                      >
                        <Zap className="w-4 h-4 mr-1" />
                        {power}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Select field option config — pick which options apply to this product */}
            {(() => {
              const selectFields = (selectedType?.default_test_input_fields || []).filter(
                f => f.type === "select" && f.options?.length > 0
              );
              if (selectFields.length === 0) return null;
              return (
                <div className="space-y-3 border-t pt-4">
                  {selectFields.map((field) => {
                    const allowed = (formData.field_option_config || {})[field.key] || [];
                    const toggle = (opt) => {
                      const next = allowed.includes(opt)
                        ? allowed.filter(o => o !== opt)
                        : [...allowed, opt];
                      setFormData(prev => ({
                        ...prev,
                        field_option_config: { ...(prev.field_option_config || {}), [field.key]: next }
                      }));
                    };
                    return (
                      <div key={field.key} className="space-y-2">
                        <Label>{field.label}</Label>
                        <div className="flex gap-2 flex-wrap">
                          {field.options.map((opt) => (
                            <Button
                              key={opt}
                              type="button"
                              variant={allowed.includes(opt) ? "default" : "outline"}
                              size="sm"
                              onClick={() => toggle(opt)}
                            >
                              {opt}
                            </Button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}

            {/* Optional field visibility — allow disabling non-required fields for this product */}
            {(() => {
              const optionalFields = (selectedType?.default_test_input_fields || []).filter(f => !f.required);
              if (optionalFields.length === 0) return null;
              const disabledSet = new Set(formData.disabled_fields || []);
              const toggle = (key) => {
                const next = disabledSet.has(key)
                  ? (formData.disabled_fields || []).filter(k => k !== key)
                  : [...(formData.disabled_fields || []), key];
                setFormData(prev => ({ ...prev, disabled_fields: next }));
              };
              return (
                <div className="space-y-2 border-t pt-4">
                  <Label className="text-sm font-semibold">
                    Optional Fields
                    <span className="text-xs text-muted-foreground font-normal ml-2">Toggle off to hide from test creation</span>
                  </Label>
                  <div className="flex flex-wrap gap-2">
                    {optionalFields.map((f) => {
                      const disabled = disabledSet.has(f.key);
                      return (
                        <button
                          key={f.key}
                          type="button"
                          onClick={() => toggle(f.key)}
                          className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border transition-colors ${
                            disabled
                              ? "bg-muted text-muted-foreground border-border line-through opacity-60"
                              : "bg-background text-foreground border-border hover:border-primary"
                          }`}
                        >
                          {disabled ? <X className="w-3 h-3 shrink-0" /> : <Check className="w-3 h-3 shrink-0 text-emerald-500" />}
                          {f.label}
                          <span className="text-muted-foreground capitalize">({f.type})</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Report Name */}
            {selectedType?.report_template && (
              <div className="space-y-1 border-t pt-4">
                <Label className="text-sm font-semibold">Report Name</Label>
                <Input
                  placeholder={[formData.model_name, formData.name].filter(Boolean).join(" ") + (selectedType?.report_template?.title ? ` - ${selectedType.report_template.title}` : "") || "e.g. SGC1 Sample Gas Cooler - Inspection Report"}
                  value={formData.report_name}
                  onChange={(e) => setFormData({ ...formData, report_name: e.target.value })}
                />
                <p className="text-xs text-muted-foreground">
                  Leave blank to auto-generate: <span className="font-mono">{[formData.model_name, formData.name].filter(Boolean).join(" ")}{selectedType?.report_template?.title ? ` - ${selectedType.report_template.title}` : ""}</span>
                </p>
              </div>
            )}

            {/* QC Session Field Mappings */}
            {(() => {
              // A "configure last column" grid expands into one virtual row per grid row whose
              // last-column cell is source=session_field, mapped the same way as a normal row.
              const sessionFieldRows = (selectedType?.report_template?.sections || [])
                .flatMap(s => (s.rows || []).flatMap(r => {
                  if (r.observationType === "grid" && r.grid_config?.configure_columns) {
                    const colHeaders = r.grid_config.col_headers || [];
                    const lastColIdx = colHeaders.length - 1;
                    const hdr = colHeaders[lastColIdx];
                    return (r.grid_config.rows || []).flatMap((gridRow, gi) => {
                      if (gridRow.cell_source !== "session_field") return [];
                      return [{
                        id: `${r.id}::c${lastColIdx}::r${gi}`,
                        label: `${r.label || r.id} — ${hdr || `Col ${lastColIdx + 1}`} (Row ${gi + 1})`,
                        sectionLabel: s.label || s.key,
                      }];
                    });
                  }
                  if (r.source === "session_field") {
                    return [{ ...r, sectionLabel: s.label || s.key }];
                  }
                  return [];
                }));
              if (!selectedType?.report_template || sessionFieldRows.length === 0) return null;
              const customFields = selectedType?.default_test_input_fields || [];
              const builtinFields = [
                ...(selectedType?.require_serial_number !== false ? [{ key: "serial_number", label: "Serial No. (session)" }] : []),
                { key: "tester_name", label: "Tester / Engineer Name" },
                ...(selectedType?.default_power_options?.length ? [{ key: "power_supply", label: "Power Supply" }] : []),
              ];
              return (
                <div className="space-y-2 border-t pt-4">
                  <Label className="text-sm font-semibold">
                    QC Report — Session Field Mappings
                    <span className="text-xs text-muted-foreground font-normal ml-2">
                      Map QC rows to session fields
                    </span>
                  </Label>
                  {sessionFieldRows.map((row) => (
                    <div key={row.id} className="flex items-center gap-3 bg-muted/40 rounded-lg px-3 py-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium truncate">{row.label}</p>
                        <p className="text-xs text-muted-foreground">{row.sectionLabel}</p>
                      </div>
                      <Select
                        value={formData.qc_session_mappings?.[row.id] || ""}
                        onValueChange={(val) => setFormData({
                          ...formData,
                          qc_session_mappings: { ...formData.qc_session_mappings, [row.id]: val }
                        })}
                      >
                        <SelectTrigger className="h-8 text-xs w-52">
                          <SelectValue placeholder="Select field…" />
                        </SelectTrigger>
                        <SelectContent>
                          {builtinFields.length > 0 && (
                            <>
                              <div className="px-2 py-1 text-xs text-muted-foreground font-medium">Session Fields</div>
                              {builtinFields.map((f) => (
                                <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>
                              ))}
                            </>
                          )}
                          {customFields.length > 0 && (
                            <>
                              <div className="px-2 py-1 text-xs text-muted-foreground font-medium">Test Input Fields</div>
                              {customFields.map((f) => (
                                <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>
                              ))}
                            </>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              );
            })()}

            {/* Tolerance Configuration */}
            <div className="space-y-3 border-t pt-4">
              <Label className="text-sm font-semibold">AI Tolerance Configuration</Label>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Defect Sensitivity</Label>
                  <Select value={formData.tolerance_config?.defect_sensitivity || "normal"} onValueChange={(v) => setFormData({...formData, tolerance_config: {...formData.tolerance_config, defect_sensitivity: v}})}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="lenient">Lenient (major defects only)</SelectItem>
                      <SelectItem value="normal">Normal (ignore dust)</SelectItem>
                      <SelectItem value="strict">Strict (flag everything)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">OCR Match Mode</Label>
                  <Select value={formData.tolerance_config?.ocr_match_mode || "format_only"} onValueChange={(v) => setFormData({...formData, tolerance_config: {...formData.tolerance_config, ocr_match_mode: v}})}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="presence">Presence (text exists)</SelectItem>
                      <SelectItem value="format_only">Format (readable & correct format)</SelectItem>
                      <SelectItem value="exact">Exact (must match precisely)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Assembly Check</Label>
                  <Select value={formData.tolerance_config?.assembly_check || "strict"} onValueChange={(v) => setFormData({...formData, tolerance_config: {...formData.tolerance_config, assembly_check: v}})}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="visual">Visual (component visible)</SelectItem>
                      <SelectItem value="strict">Strict (must be installed in position)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
<div className="space-y-1">
                  <Label className="text-xs">Calibration Tolerance (%)</Label>
                  <Input type="number" className="h-8 text-xs" value={formData.tolerance_config?.calibration_tolerance_percent || 5} onChange={(e) => setFormData({...formData, tolerance_config: {...formData.tolerance_config, calibration_tolerance_percent: Number(e.target.value)}})} />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Custom Rules</Label>
                <Textarea className="text-xs resize-none" rows={2} placeholder="e.g., For single-channel units, only verify chlorine..." value={formData.tolerance_config?.custom_rules || ""} onChange={(e) => setFormData({...formData, tolerance_config: {...formData.tolerance_config, custom_rules: e.target.value}})} />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button onClick={handleSubmit} disabled={submitting} data-testid="save-product-btn">
                {submitting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : null}
                {editingProduct ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Products Table */}
      <Card>
        <CardContent className="p-0">
          {products.length === 0 ? (
            <div className="text-center py-12">
              <Package className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">No products yet</p>
              <p className="text-sm text-muted-foreground">Add your first product to get started</p>
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-center py-12">
              <Search className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">No products match "{searchQuery}"</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product</TableHead>
                  <TableHead>Model No.</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Power Options</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredProducts.map((product) => {
                  const IconComp = getIconForType(product);
                  return (
                    <TableRow key={product.id} data-testid={`product-row-${product.id}`}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{product.name}</p>
                          <p className="text-sm text-muted-foreground truncate max-w-[300px]">
                            {product.description}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {product.model_name || <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="gap-1">
                          <IconComp className="w-3 h-3" />
                          {product.product_type_name || product.product_type?.replace("_", " ")}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {product.power_options && product.power_options.length > 0 ? (
                          <div className="flex gap-1 flex-wrap">
                            {product.power_options.map((p) => (
                              <Badge key={p} variant="secondary" className="text-xs">{p}</Badge>
                            ))}
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-sm">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {new Date(product.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenDialog(product)}
                            data-testid={`edit-product-${product.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:text-destructive"
                            onClick={() => handleDelete(product.id)}
                            data-testid={`delete-product-${product.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
